// ABOUTME: Application entry point for Oleum BI frontend (Goldman POC).
// ABOUTME: Renders the React app with all providers (Auth0, Router, Context).

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { AppProvider } from '@/context/AppContext';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { router } from '@/router';
import '@/styles/globals.css';

// Auth0 configuration from environment variables
const auth0Config = {
  domain: import.meta.env.VITE_AUTH0_DOMAIN || 'dev-obsidianbi.auth0.com',
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || 'development-client-id',
  authorizationParams: {
    redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI || `${window.location.origin}/auth/callback`,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE || 'https://api.obsidianbi.com',
  },
  // Store tokens in localStorage to persist across page refreshes
  cacheLocation: 'localstorage' as const,
  // Use refresh tokens for longer sessions
  useRefreshTokens: true,
  // Fallback to iframe-based token refresh if refresh token fails
  useRefreshTokensFallback: true,
  // Add onRedirectCallback for debugging
  onRedirectCallback: (appState?: { returnTo?: string }) => {
    console.log('[Auth0] onRedirectCallback called');
    console.log('[Auth0] appState:', appState);
    window.history.replaceState({}, document.title, appState?.returnTo || window.location.pathname);
  },
};

// Debug: Log Auth0 configuration at startup
console.log('='.repeat(60));
console.log('[main.tsx] Auth0 Configuration Debug');
console.log('='.repeat(60));
console.log('[main.tsx] VITE_AUTH0_DOMAIN:', import.meta.env.VITE_AUTH0_DOMAIN);
console.log('[main.tsx] VITE_AUTH0_CLIENT_ID:', import.meta.env.VITE_AUTH0_CLIENT_ID);
console.log('[main.tsx] VITE_AUTH0_AUDIENCE:', import.meta.env.VITE_AUTH0_AUDIENCE);
console.log('[main.tsx] VITE_AUTH0_REDIRECT_URI:', import.meta.env.VITE_AUTH0_REDIRECT_URI);
console.log('[main.tsx] VITE_USE_MOCK_AUTH:', import.meta.env.VITE_USE_MOCK_AUTH);
console.log('[main.tsx] Full auth0Config:', JSON.stringify(auth0Config, null, 2));

// Debug: Check localStorage for cached Auth0 data
const cacheKey = `@@auth0spajs@@::${auth0Config.clientId}::${auth0Config.authorizationParams.audience}::openid profile email offline_access`;
console.log('[main.tsx] Looking for cache key:', cacheKey);
const cachedData = localStorage.getItem(cacheKey);
if (cachedData) {
  try {
    const parsed = JSON.parse(cachedData);
    console.log('[main.tsx] Found cached Auth0 data:');
    console.log('[main.tsx]   - Has body:', !!parsed.body);
    console.log('[main.tsx]   - Has decodedToken:', !!parsed.decodedToken);
    if (parsed.body) {
      console.log('[main.tsx]   - access_token exists:', !!parsed.body.access_token);
      console.log('[main.tsx]   - refresh_token exists:', !!parsed.body.refresh_token);
      console.log('[main.tsx]   - id_token exists:', !!parsed.body.id_token);
      console.log('[main.tsx]   - expires_in:', parsed.body.expires_in);
      if (parsed.body.access_token) {
        try {
          const tokenParts = parsed.body.access_token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const exp = new Date(payload.exp * 1000);
            const now = new Date();
            console.log('[main.tsx]   - Token exp:', exp.toISOString());
            console.log('[main.tsx]   - Current time:', now.toISOString());
            console.log('[main.tsx]   - Token expired:', exp < now);
            console.log('[main.tsx]   - Time until expiry:', Math.round((exp.getTime() - now.getTime()) / 1000 / 60), 'minutes');
          }
        } catch (e) {
          console.log('[main.tsx]   - Could not decode token:', e);
        }
      }
    }
  } catch (e) {
    console.log('[main.tsx] Could not parse cached data:', e);
  }
} else {
  console.log('[main.tsx] No cached Auth0 data found');
  // List all localStorage keys that might be Auth0 related
  console.log('[main.tsx] All localStorage keys containing "auth0":');
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.toLowerCase().includes('auth0')) {
      console.log('[main.tsx]   -', key);
    }
  }
}
console.log('='.repeat(60));

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <Auth0Provider {...auth0Config}>
        <AuthProvider>
          <AppProvider>
            <RouterProvider router={router} />
          </AppProvider>
        </AuthProvider>
      </Auth0Provider>
    </ThemeProvider>
  </StrictMode>
);
